import type { Game, LeaderboardEntry, User } from '@/lib/types';

export function calculateLeaderboard(
  users: User[],
  games: Game[],
  options?: {
    startDate?: Date;
    endDate?: Date;
    includeGuests?: boolean;
  }
): LeaderboardEntry[] {
  const { startDate, endDate, includeGuests = true } = options ?? {};

  const filteredGames = games.filter(g => {
    if (g.is_deleted || (g.status !== 'archived' && g.status !== 'settled' && g.status !== 'settling')) return false;
    const gameDate = new Date(g.date);
    if (startDate && gameDate < startDate) return false;
    if (endDate && gameDate > endDate) return false;
    return true;
  });

  const stats = new Map<string, {
    total_pnl: number;
    games_played: number;
    wins: number;
    losses: number;
    best_night: number;
    worst_night: number;
    total_buy_in: number;
    streak_count: number;
    streak_type: 'win' | 'loss' | null;
    last_streak: 'win' | 'loss' | null;
    longest_win_streak: number;
    longest_loss_streak: number;
    pnl_values: number[];
  }>();

  for (const user of users) {
    if (!user.is_active) continue;
    if (!includeGuests && !user.is_regular) continue;
    stats.set(user.id, {
      total_pnl: 0,
      games_played: 0,
      wins: 0,
      losses: 0,
      best_night: 0,
      worst_night: 0,
      total_buy_in: 0,
      streak_count: 0,
      streak_type: null,
      last_streak: null,
      longest_win_streak: 0,
      longest_loss_streak: 0,
      pnl_values: [],
    });
  }

  const sortedGames = [...filteredGames].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  for (const game of sortedGames) {
    for (const player of game.players) {
      const s = stats.get(player.user_id);
      if (!s) continue;

       const net = (game.settlement?.player_inputs[player.user_id]?.final_stack || 0) - (player.total_bought_in || 0);

      s.total_pnl += net;
      s.games_played++;
      s.total_buy_in += player.total_bought_in;
      s.pnl_values.push(net);

      if (net > 0) {
        s.wins++;
        s.best_night = Math.max(s.best_night, net);
        if (s.last_streak === 'win') {
          s.streak_count++;
        } else {
          s.streak_count = 1;
          s.streak_type = 'win';
        }
        s.last_streak = 'win';
      } else if (net < 0) {
        s.losses++;
        s.worst_night = Math.min(s.worst_night, net);
        if (s.last_streak === 'loss') {
          s.streak_count++;
        } else {
          s.streak_count = 1;
          s.streak_type = 'loss';
        }
        s.last_streak = 'loss';
      }

      // Track all-time longest win/loss streaks
      if (s.longest_win_streak < s.streak_count && s.streak_type === 'win') {
        s.longest_win_streak = s.streak_count;
      }
      if (s.longest_loss_streak < s.streak_count && s.streak_type === 'loss') {
        s.longest_loss_streak = s.streak_count;
      }
    }
  }

  const entries: LeaderboardEntry[] = [];
  for (const [userId, s] of stats) {
    const user = users.find(u => u.id === userId);
    if (!user) continue;

    // Consistency: std dev of PnL values
    let consistency: 'high' | 'medium' | 'low' = 'medium';
    if (s.pnl_values.length >= 3) {
      const mean = s.pnl_values.reduce((a, b) => a + b, 0) / s.pnl_values.length;
      const variance = s.pnl_values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / s.pnl_values.length;
      const stdDev = Math.sqrt(variance);
      const meanAbs = Math.abs(mean) || 1;
      const cv = stdDev / meanAbs;
      consistency = cv < 1.5 ? 'high' : cv < 3 ? 'medium' : 'low';
    }

    entries.push({
      user_id: userId,
      name: user.name,
      photo_url: user.custom_photo_url ?? user.photo_url,
      total_pnl: s.total_pnl,
      games_played: s.games_played,
      wins: s.wins,
      losses: s.losses,
      best_night: s.best_night,
      worst_night: s.worst_night,
      avg_buy_in: s.games_played > 0 ? s.total_buy_in / s.games_played : 0,
      current_streak: s.streak_type === null ? 0 : s.streak_count,
      streak_type: s.streak_type,
      roi: s.games_played > 0 && s.total_buy_in > 0
        ? Math.round((s.total_pnl / s.total_buy_in) * 100)
        : 0,
      longestWinStreak: s.longest_win_streak,
      longestLossStreak: s.longest_loss_streak,
      consistency,
    });
  }

  return entries.sort((a, b) => {
    if (b.total_pnl !== a.total_pnl) return b.total_pnl - a.total_pnl;
    if (a.games_played !== b.games_played) return a.games_played - b.games_played;
    return a.name.localeCompare(b.name);
  });
}
